import "./styles.less";

import { Button, Col, Divider, Form, Input, Row, Space, Typography } from "antd";
import { useContext, useEffect, useState } from "react";

import { Logo } from "../../Logo/web";
import { ButtonType } from "antd/lib/button";
import { ConfigStateContext } from "../../../Contexts/Configurations/context";
import { FacebookLogin } from "./FacebookLogin/web";
import { GoogleLogin } from "./GoogleLogin/web";
import { ModalType } from "../AuthModal/web";
import cookie from "js-cookie";
import { useUser } from "../User.hook";

type LoginStepType = "first" | "login" | "register" | "password" | "passwordSuccess";

const Login = ({
	onOk = () => {},
	email,
	modalType = "LogIn",
}: {
	onOk?: Function;
	email?: string;
	modalType?: ModalType;
}) => {
	const {
		login,
		register,
		userExists,
		forgotPassword,
		userInput: input,
		validationErrors: { errors, set: setErrors },
		socialLogin,
	} = useUser();

	const [step, setStep] = useState<LoginStepType>("first");
	const { country_code } = useContext(ConfigStateContext);

	const callbackLogin = data => {
		cookie.set("frontend_token", data.access_token, { expires: 365 });
		localStorage.setItem("user_md5", data.user_md5);
		onOk(data);
	};

	useEffect(() => {
		if (email) {
			input.set({ ...input.value, email: email });
			setStep(modalType == "LogIn" ? "login" : "register");
		}
	}, []);

	useEffect(() => {
		if (login.response.data && !login.response.loading) {
			callbackLogin(login.response.data.login);
		} else if (register.response.data && !register.response.loading) {
			callbackLogin(register.response.data.registerIndividual);
		} else if (socialLogin.response.data && !socialLogin.response.loading) {
			callbackLogin(socialLogin.response.data.socialLogin);
		} else if (forgotPassword.response.data && !forgotPassword.response.loading) {
			setStep("passwordSuccess");
		}
	}, [login.response, register.response, socialLogin.response, forgotPassword.response]);

	useEffect(() => {
		if (!userExists.response.loading) {
			if (userExists.response.data) setStep("login");
			else if (
				userExists.response.error &&
				userExists.response.error?.graphQLErrors[0]?.extensions?.category == "NotFound"
			)
				setStep("register");
		}
	}, [userExists.response]);

	useEffect(() => setErrors([]), [step]);

	const buttonAction = () => {
		if (step == "first") userExists.send();
		else if (step == "login") login.send();
		else if (step == "register") register.send();
		else if (step == "password") forgotPassword.send();
		else if (step == "passwordSuccess") onOk();
	};

	const Title = () => {
		let res = "Ingresá o Registrate para ver tu actividad";
		if (step == "login") res = "¡Bienvenido! Ingresá tu contraseña para continuar";
		else if (step == "register") res = "Crea una contraseña para continuar";
		else if (step == "password")
			res = "¿Olvidaste tu contraseña? Ingresá tu correo electronico para reiniciarla";
		else if (step == "passwordSuccess")
			res = "¡Gracias! Revisá tu correo electrónico para reiniciar tu contraseña.";

		return <Typography.Title level={4}>{res}</Typography.Title>;
	};

	const EmailInput = (
		<Form.Item
			id="email"
			name="email"
			label="Correo electrónico"
			normalize={(value, prevValue, prevValues) => value.trim()}
			rules={[
				{ required: true, message: "Por favor ingrese un correo" },
				{
					pattern: /^[A-Z0-9._%+-\s]+@[A-Z0-9.-\s]+\.[A-Z]{2,4}$/i,
					message: "Por favor ingrese un correo válido",
				},
			]}>
			<Input
				className="input-email"
				placeholder="Ingresá tu correo electrónico"
				value={input.value.email}
				autoFocus
				onChange={e => input.set({ ...input.value, email: e.target.value })}
				// autoFocus
			/>
		</Form.Item>
	);

	const PasswordInput = (
		<>
			<Form.Item
				name={"password"}
				label="Contraseña"
				rules={[
					{ required: true, message: "Ingrese una contraseña" },
					step === "register"
						? {
								min: 8,
								message: `Las contraseñas deben de tener al menos 8 caracteres`,
						  }
						: {},
				]}>
				<Input.Password
					className="input-password"
					placeholder={
						step === "register"
							? "Ingresá una contraseña, con al menos 8 caracteres"
							: "Ingrese una contraseña"
					}
					value={input.value.password}
					autoFocus
					onChange={e => input.set({ ...input.value, password: e.target.value })}
				/>
			</Form.Item>
		</>
	);

	const FormButton = () => {
		let text = "Continuar";
		let type: ButtonType = step == "passwordSuccess" ? "default" : "primary";
		if (step == "login") text = "Enviar";
		else if (step == "register") text = "Guardar Contraseña";
		else if (step == "password") text = "Enviar";
		else if (step == "passwordSuccess") text = "Cerrar";

		return (
			<Button
				className="btn-access"
				type={type}
				htmlType="submit"
				loading={
					login.response.loading ||
					register.response.loading ||
					userExists.response.loading ||
					forgotPassword.response.loading
				}>
				{text}
			</Button>
		);
	};

	const errorMessage = (
		<>
			{errors.map((o, i) => (
				<Typography.Text type="danger" key={"error" + i}>
					{o}
				</Typography.Text>
			))}
		</>
	);

	const GoogleButton = <GoogleLogin onClick={response => socialLogin.send(response)} />;

	const FacebookButton = <FacebookLogin onClick={response => socialLogin.send(response)} />;

	const AgentButton = (
		<Space direction={"vertical"} size={0}>
			<Typography.Text className="modal_footer_text">
				¿Sos una inmobiliaria?
			</Typography.Text>
			<Typography.Link
				className="modal_footer_link"
				href={"/soyinmobiliaria"}
				strong>
				Regístrate aquí
			</Typography.Link>
		</Space>
	);

	const PassButton = (
		<Space direction={"vertical"} size={0}>
			<Typography.Text>¿Olvidaste tu contraseña?</Typography.Text>
			<Typography.Link onClick={() => setStep("password")} strong>
				Ingresá aquí
			</Typography.Link>
		</Space>
	);

	const BackLoginButton = (
		<Space direction={"vertical"} size={0}>
			<Typography.Text>¿Recordás tu contraseña?</Typography.Text>
			<Typography.Link onClick={() => setStep("login")} strong>
				Ingresá aquí
			</Typography.Link>
		</Space>
	);

	const DoLaterButton = (
		<Typography.Link strong onClick={() => register.doLater()}>
			Hacer esto despúes
		</Typography.Link>
	);

	const SuccesPasswordText = (
		<Typography.Text>{forgotPassword.response.data?.forgotPassword}</Typography.Text>
	);

	return (
		<Row justify={"center"} gutter={[0, 0]} className="auth-content">
			<Col className="brand login-space">
				<Logo type="desktop"/>
			</Col>

			<Col className="title" span={24}>
				<Title />
			</Col>

			{step == "passwordSuccess" && <Col span={24}>{SuccesPasswordText}</Col>}

			<Col span={24}>
				<Form
					className="auth-form"
					layout={"vertical"}
					hideRequiredMark
					onFinish={buttonAction}>
					{errors.length > 0 && errorMessage}
					{(step == "first" || step == "password") && EmailInput}
					{(step == "login" || step == "register") && PasswordInput}
					<FormButton />
				</Form>
			</Col>

			{step == "first" && <Divider plain>{"O"}</Divider>}
			{step == "first" && (
				<Col span={24} className="login-space">
					{FacebookButton}
				</Col>
			)}
			{step == "first" && <Col span={24}>{GoogleButton}</Col>}

			{step != "passwordSuccess" && <Divider />}
			<Col span={24} className="bottom-content">
				{step == "first" && country_code != 'ce3' && AgentButton}
				{step == "login" && PassButton}
				{step == "password" && BackLoginButton}
				{step == "register" && DoLaterButton}
			</Col>
		</Row>
	);
};

export { Login };

